import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { callAPI } from '../helper/apiUtils';
import { requestDirectMessage } from '../helper/directMessagePrompt';
import { BASE_URL } from '../helper/constants';
import useTranslation from '../hooks/useTranslation';
import './LikesDropdown.css';

function LikesDropdown({ 
  articleId, 
  likes = [], 
  likeCount = 0, 
  children,
  isInternal = false,
  // Bumped by an outside control (the News card menu's "Show likes") to open the list.
  openSignal = 0
}) {
  const { t, isGerm } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasLoadedFromApi, setHasLoadedFromApi] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const dropdownRef = useRef(null);
  const dropdownPanelRef = useRef(null);

  const updatePosition = () => {
    if (dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 8,
        left: rect.left,
      });
    }
  };

  // Position dropdown when opened; update on scroll/resize when open
  useEffect(() => {
    if (!isOpen) return;
    updatePosition();
    const onScrollOrResize = () => updatePosition();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [isOpen]);

  // Close dropdown when clicking outside (trigger or portaled panel)
  useEffect(() => {
    const handleClickOutside = (event) => {
      const inTrigger = dropdownRef.current && dropdownRef.current.contains(event.target);
      const inPanel = dropdownPanelRef.current && dropdownPanelRef.current.contains(event.target);
      if (!inTrigger && !inPanel) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      // Delay attaching the click-outside listener to avoid the same click that opened the dropdown
      // from immediately closing it
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchstart', handleClickOutside);
      }, 0);

      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('touchstart', handleClickOutside);
      };
    }
  }, [isOpen]);

  // Close dropdown on escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  // Sync dropdown list when likes prop changes (e.g. after like/unlike)
  const likesKey = likeCount + '-' + (Array.isArray(likes) ? likes.map(l => l?.id).filter(Boolean).join(',') : '');
  useEffect(() => {
    const next = Array.isArray(likes) ? likes : [likes].filter(Boolean);
    setUsers(next);
  }, [likesKey, likeCount]);

  const fetchUsers = async () => {
    if (hasLoadedFromApi || loading) return;
    
    setLoading(true);
    try {
      const { data } = await callAPI(`/article/${articleId}/listOfLikes`, {}, 'GET');
      if (data?.users) {
        // Reverse the users array so the last user who liked appears first
        // API returns in ascending order (oldest first), we want descending (newest first)
        const reversedUsers = [...data.users].reverse();
        setUsers(reversedUsers);
        setHasLoadedFromApi(true);
      }
    } catch (error) {
      console.error('Failed to fetch likes:', error);
      // Fallback to existing likes data (already in correct order from handleLike)
      const likesArray = Array.isArray(likes) ? likes : [likes].filter(Boolean);
      setUsers(likesArray);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!isOpen) {
      // If we don't have full data from API yet, fetch it
      if (!hasLoadedFromApi && likeCount > 0) {
        fetchUsers();
      } else if (!hasLoadedFromApi) {
        // Use existing likes data
        const likesArray = Array.isArray(likes) ? likes : [likes].filter(Boolean);
        setUsers(likesArray);
      }
    }
    
    setIsOpen(!isOpen);
  };

  // A card that remounts (virtualised list) keeps its last signal; only a change opens the list.
  const mountSignal = useRef(openSignal);
  useEffect(() => {
    if (!openSignal || openSignal === mountSignal.current || isOpen) return;
    if (!hasLoadedFromApi && likeCount > 0) fetchUsers();
    setIsOpen(true);
    // Only a new signal opens the list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openSignal]);

  const getUserDetails = (person) => {
    setIsOpen(false);
    const userId = person?.id || person?.userId || person;
    if (!userId) return;
    requestDirectMessage({
      id: userId,
      fullName: person?.fullName || `${person?.firstName || ""} ${person?.lastName || ""}`.trim(),
      avatar: person?.avatar,
    });
  };

  const getAvatarUrl = (likeUser) => {
    const avatar = likeUser?.avatar || likeUser?.userAvatar || likeUser?.userImage || 
                   likeUser?.userProfileImage || likeUser?.postedByImage || likeUser?.profileImage;
    
    if (!avatar) return null;
    
    if (typeof avatar === 'string' && (avatar.startsWith('http://') || avatar.startsWith('https://'))) {
      return avatar;
    }
    
    if (typeof avatar === 'string' && avatar.includes('/getFile?filePath=')) {
      if (avatar.startsWith('http://') || avatar.startsWith('https://')) {
        return avatar;
      }
      const filePathMatch = avatar.match(/filePath=(.+)/);
      if (filePathMatch) {
        return BASE_URL + decodeURIComponent(filePathMatch[1]);
      }
      return avatar;
    }
    
    return BASE_URL + avatar;
  };

  const displayUsers = hasLoadedFromApi ? users : (Array.isArray(likes) ? likes : [likes].filter(Boolean));

  return (
    <div 
      ref={dropdownRef}
      className="likes-dropdown-container likes-dropdown__container"
    >
      <div onClick={handleToggle} className="kj-pointer">
        {children}
      </div>
      
      {isOpen && ReactDOM.createPortal(
        <div
          ref={dropdownPanelRef}
          className="likes-dropdown likes-dropdown-portal likes-dropdown__panel"
          style={{
            top: dropdownPosition.top,
            left: dropdownPosition.left,
          }}
        >
          <div 
            className="likes-dropdown-header likes-dropdown__header"
          >
            {t('people_who_like_article') || 'People who liked this'}
          </div>
          
          <div className="likes-dropdown-list likes-dropdown__list">
            {loading ? (
              <div className="likes-dropdown__state">
                <div className={`loading-spinner likes-dropdown__spinner${isInternal ? ' likes-dropdown__spinner--internal' : ''}`} />
                Loading...
              </div>
            ) : displayUsers.length === 0 ? (
              <div className="likes-dropdown__state">
                {t('no_likes_yet') || 'No likes yet'}
              </div>
            ) : (
              displayUsers.map((likeUser, index) => (
                <div 
                  key={likeUser?.id || `like-${index}`}
                  className="likes-dropdown-item likes-dropdown__item"
                  onClick={() => getUserDetails(likeUser)}
                  onMouseEnter={(e) => e.currentTarget.querySelector('.likes-dropdown-name').style.color = isInternal ? 'var(--kjuup-internal)' : 'var(--kjuup-accent)'}
                  onMouseLeave={(e) => e.currentTarget.querySelector('.likes-dropdown-name').style.color = '#333'}
                >
                  <img
                    width={32}
                    height={32}
                    src={getAvatarUrl(likeUser) || process.env.PUBLIC_URL + "/assets/img/head.svg"}
                    alt={likeUser?.fullName || likeUser?.userFullName || 'User'}
                    onError={(e) => { e.target.onerror = null; e.target.src = process.env.PUBLIC_URL + "/assets/img/head.svg"; }}
                    className="likes-dropdown__avatar"
                  />
                  <div 
                    className="likes-dropdown-name likes-dropdown__name"
                  >
                    {likeUser?.fullName || likeUser?.userFullName || 
                     (likeUser?.firstName && likeUser?.lastName ? 
                       `${likeUser.firstName} ${likeUser.lastName}` : 'User')}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>,
        document.body
      )}
      
    </div>
  );
}

export default LikesDropdown;
